import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Typography, Card, CardContent, Button, Stack, CircularProgress, Chip, } from '@mui/material';
import { Work as WorkIcon } from '@mui/icons-material';
import { JobType, JobLocationType, ShiftType } from '@common/clientServerApi';
// ============ Helper Functions ============
const getJobTypeTags = (template) => {
    const tags = [];
    // Add salary range if available
    if (template.salaryRange) {
        const { min, max, currency } = template.salaryRange;
        const formatSalary = (val) => val >= 1000 ? `${Math.round(val / 1000)}k` : val.toString();
        tags.push(`${currency === 'USD' ? '$' : currency === 'GBP' ? '£' : currency}${formatSalary(min)} - ${formatSalary(max)}`);
    }
    // Add job type
    if (template.jobType) {
        const jobTypeMap = {
            [JobType.FullTime]: 'Full-time',
            [JobType.PartTime]: 'Part-time',
            [JobType.Contract]: 'Contract',
            [JobType.Temporary]: 'Temporary',
            [JobType.Seasonal]: 'Seasonal',
            [JobType.Internship]: 'Internship',
        };
        tags.push(jobTypeMap[template.jobType] || template.jobType);
    }
    // Add schedule if available
    if (template.schedule) {
        tags.push(template.schedule);
    }
    return tags;
};
// ============ Sub-Components ============
const JobTitleHeader = ({ title }) => (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5 }, children: [_jsx(Box, { sx: {
                width: 40,
                height: 40,
                borderRadius: 1,
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }, children: _jsx(WorkIcon, { sx: { color: 'grey.600' } }) }), _jsx(Typography, { variant: "h3", children: title })] }));
const TagChips = ({ tags }) => (_jsx(Stack, { direction: "row", spacing: 1, flexWrap: "wrap", sx: { gap: 0.5 }, children: tags.map((tag, index) => (_jsx(Chip, { label: tag, size: "small", sx: {
            bgcolor: 'grey.100',
            color: 'text.primary',
            fontSize: '0.75rem',
            height: 24,
        } }, index))) }));
const SectionTitle = ({ children }) => (_jsx(Typography, { variant: "h4", sx: { mb: 1.5 }, children: children }));
const BulletList = ({ items }) => (_jsx(Box, { component: "ul", sx: { m: 0, pl: 2.5 }, children: items.map((item, index) => (_jsx(Typography, { component: "li", variant: "body2", sx: { mb: 0.5, color: 'text.primary' }, children: item }, index))) }));
const DetailRow = ({ label, value }) => (_jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between' }, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: label }), _jsx(Typography, { variant: "body2", color: "text.primary", fontWeight: 500, children: value })] }));
const JobDetailsGrid = ({ template }) => {
    const jobTypeLabel = template.jobType === JobType.FullTime ? 'Full time' :
        template.jobType === JobType.PartTime ? 'Part time' :
            template.jobType === JobType.Contract ? 'Contract' : 'Full time';
    const shiftLabel = template.shiftType === ShiftType.Day ? 'Day shift' :
        template.shiftType === ShiftType.Night ? 'Night shift' :
            template.shiftType === ShiftType.Variable ? 'Variable' : 'Standard hours';
    const locationTypeLabel = template.locationType === JobLocationType.InPerson ? 'In person' :
        template.locationType === JobLocationType.FullyRemote ? 'Remote' :
            template.locationType === JobLocationType.Hybrid ? 'Hybrid' :
                template.locationType === JobLocationType.OnRoad ? 'On road' : 'In person';
    const experienceLabel = template.minExperienceYears !== undefined
        ? `${template.minExperienceYears} year${template.minExperienceYears !== 1 ? 's' : ''}`
        : 'Not specified';
    return (_jsxs(Stack, { spacing: 1.5, children: [_jsx(DetailRow, { label: "Job Type", value: jobTypeLabel }), _jsx(DetailRow, { label: "Shift", value: shiftLabel }), _jsx(DetailRow, { label: "Location", value: template.location }), _jsx(DetailRow, { label: "Location type", value: locationTypeLabel }), _jsx(DetailRow, { label: "Min experience", value: experienceLabel })] }));
};
const ActionButtons = ({ onConfirm, onEdit, loading }) => (_jsxs(Stack, { spacing: { xs: 1.5, sm: 4 }, sx: { mt: { xs: 2, sm: 4 } }, children: [_jsx(Button, { variant: "contained", fullWidth: true, onClick: onConfirm, disabled: loading, sx: {
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
            }, children: loading ? (_jsx(CircularProgress, { size: 24, color: "inherit" })) : ('Looks good, let\'s upload resumes') }), _jsx(Button, { variant: "outlined", fullWidth: true, onClick: onEdit, disabled: loading, sx: {
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '1rem',
                borderColor: 'grey.300',
                color: 'text.primary',
            }, children: "Edit job details" })] }));
// ============ Main Component ============
export const JobReviewStep = ({ template, onConfirm, onEdit, loading = false, }) => {
    const jobTypeTags = getJobTypeTags(template);
    const allTags = [...jobTypeTags, ...(template.benefits || [])];
    return (_jsx(Box, { sx: { bgcolor: { xs: 'background.paper', sm: 'grey.50' }, pb: { xs: 2, sm: 4 } }, children: _jsxs(Box, { sx: { px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 } }, children: [_jsx(Typography, { variant: "h2", sx: { my: { xs: 2, sm: 5 }, textAlign: 'center' }, children: "Great! Review & Save Job" }), _jsx(Card, { sx: {
                        border: { xs: 'none', sm: '1px solid' },
                        borderColor: 'grey.200',
                        borderRadius: { xs: 0, sm: 2 },
                        mb: { xs: 2, sm: 3 },
                        boxShadow: { xs: 'none', sm: undefined },
                    }, children: _jsx(CardContent, { sx: { p: { xs: 0, sm: 5 }, '&:last-child': { pb: { xs: 0, sm: 5 } } }, children: _jsxs(Stack, { spacing: { xs: 2.5, sm: 5 }, children: [_jsx(JobTitleHeader, { title: template.title }), allTags.length > 0 && _jsx(TagChips, { tags: allTags }), template.responsibilities && template.responsibilities.length > 0 && (_jsxs(Box, { children: [_jsx(SectionTitle, { children: "Responsibilities:" }), _jsx(BulletList, { items: template.responsibilities })] })), template.requirements && template.requirements.length > 0 && (_jsxs(Box, { children: [_jsx(SectionTitle, { children: "Requirements:" }), _jsx(BulletList, { items: template.requirements })] })), template.schedule && (_jsxs(Typography, { variant: "body2", children: ["Schedule: ", template.schedule] })), _jsx(JobDetailsGrid, { template: template })] }) }) }), _jsx(ActionButtons, { onConfirm: onConfirm, onEdit: onEdit, loading: loading })] }) }));
};
